import type { GameView } from '../engine/view.js';
import type { Terrain } from '../engine/types.js';

export const TERRAIN_COLORS: Record<Terrain, string> = {
  wood: '#2f7d46',
  brick: '#c0613a',
  sheep: '#9bd36a',
  wheat: '#e9c64b',
  ore: '#8d939c',
  desert: '#e3d3a8',
};

export const TERRAIN_LABELS: Record<Terrain, string> = {
  wood: 'Wood',
  brick: 'Brick',
  sheep: 'Sheep',
  wheat: 'Wheat',
  ore: 'Ore',
  desert: 'Desert',
};

export type Highlight =
  | { kind: 'vertex'; ids: number[] }
  | { kind: 'edge'; ids: number[] }
  | { kind: 'hex'; ids: number[] }
  | null;

export interface BoardHandlers {
  onVertex(id: number): void;
  onEdge(id: number): void;
  onHex(id: number): void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
  parent?: Element,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  parent?.appendChild(node);
  return node;
}

function pips(token: number): number {
  return 6 - Math.abs(7 - token);
}

/** Draw the whole board into `svg`, replacing what was there. */
export function renderBoard(svg: SVGSVGElement, view: GameView, highlight: Highlight, handlers: BoardHandlers): void {
  const { hexes, vertices, edges, harbors } = view.board;
  svg.replaceChildren();
  svg.setAttribute('viewBox', '-5.75 -5.4 11.5 10.8');

  // Sea.
  el('circle', { cx: 0, cy: 0, r: 5.25, class: 'sea' }, svg);

  // Harbors: a pier from each harbor vertex out to a label.
  for (const h of harbors) {
    const [a, b] = edges[h.edge].vertices.map((v) => vertices[v]);
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const len = Math.hypot(mx, my);
    const ox = (mx / len) * (len + 0.75);
    const oy = (my / len) * (len + 0.75);
    const g = el('g', { class: 'harbor' }, svg);
    el('line', { x1: a.x, y1: a.y, x2: ox, y2: oy }, g);
    el('line', { x1: b.x, y1: b.y, x2: ox, y2: oy }, g);
    el('circle', { cx: ox, cy: oy, r: 0.42, fill: h.kind === 'any' ? '#fff' : TERRAIN_COLORS[h.kind] }, g);
    const t = el('text', { x: ox, y: oy + 0.04, class: 'harbor-text' }, g);
    t.textContent = h.kind === 'any' ? '3:1' : '2:1';
    const title = el('title', {}, g);
    title.textContent = h.kind === 'any' ? 'Harbor: trade any 3 for 1' : `Harbor: trade 2 ${h.kind} for 1`;
  }

  // Hexes.
  const hexHighlight = highlight?.kind === 'hex' ? new Set(highlight.ids) : null;
  for (const hex of hexes) {
    const points = hex.vertices.map((v) => `${vertices[v].x},${vertices[v].y}`).join(' ');
    const g = el('g', { class: 'hex' }, svg);
    const poly = el('polygon', { points, fill: TERRAIN_COLORS[hex.terrain], class: 'hex-shape' }, g);
    const title = el('title', {}, g);
    title.textContent = TERRAIN_LABELS[hex.terrain] + (hex.token ? ` (${hex.token})` : '');

    if (hex.token !== null) {
      const hot = hex.token === 6 || hex.token === 8;
      el('circle', { cx: hex.x, cy: hex.y, r: 0.36, class: 'token' }, g);
      const t = el('text', { x: hex.x, y: hex.y - 0.02, class: `token-text${hot ? ' hot' : ''}` }, g);
      t.textContent = String(hex.token);
      const n = pips(hex.token);
      for (let i = 0; i < n; i++) {
        el('circle', { cx: hex.x + (i - (n - 1) / 2) * 0.09, cy: hex.y + 0.2, r: 0.03, class: `pip${hot ? ' hot' : ''}` }, g);
      }
    }
    if (hex.id === view.robberHex) {
      el('circle', { cx: hex.x + (hex.token !== null ? 0.58 : 0), cy: hex.y, r: 0.2, class: 'robber' }, g);
    }
    if (hexHighlight?.has(hex.id)) {
      poly.classList.add('target');
      g.addEventListener('click', () => handlers.onHex(hex.id));
    }
  }

  // Roads.
  for (const [edgeId, player] of Object.entries(view.roads)) {
    const [a, b] = edges[Number(edgeId)].vertices.map((v) => vertices[v]);
    const color = view.players[player].color;
    el('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'road-outline' }, svg);
    el('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'road', stroke: color }, svg);
  }

  // Edge targets.
  if (highlight?.kind === 'edge') {
    for (const id of highlight.ids) {
      const [a, b] = edges[id].vertices.map((v) => vertices[v]);
      const line = el('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'edge-target' }, svg);
      line.addEventListener('click', () => handlers.onEdge(id));
    }
  }

  // Buildings.
  for (const [vertexId, b] of Object.entries(view.buildings)) {
    const v = vertices[Number(vertexId)];
    const color = view.players[b.player].color;
    const s = 0.2;
    const points =
      b.type === 'settlement'
        ? [[-s, s], [-s, -s * 0.2], [0, -s * 1.1], [s, -s * 0.2], [s, s]]
        : [[-s * 1.6, s], [-s * 1.6, -s * 0.4], [-s * 0.4, -s * 0.4], [-s * 0.4, -s * 1.2], [s * 0.4, -s * 1.8], [s * 1.2, -s * 1.2], [s * 1.2, s]];
    el('polygon', { points: points.map(([x, y]) => `${v.x + x},${v.y + y}`).join(' '), fill: color, class: 'building' }, svg);
  }

  // Vertex targets.
  if (highlight?.kind === 'vertex') {
    for (const id of highlight.ids) {
      const v = vertices[id];
      const c = el('circle', { cx: v.x, cy: v.y, r: 0.2, class: 'vertex-target' }, svg);
      c.addEventListener('click', () => handlers.onVertex(id));
    }
  }
}
